import React, { useCallback, useEffect } from 'react'

import { Controller } from 'react-hook-form'

import {
  Button,
  Icon,
  SuccessOutlined,
  TextInput,
  Toggle,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, string } from 'prop-types'

import SubscriptionEvents from 'Pods/Shared/Constants/subscriptionEvents'
import useExplicitMarketingConsent from 'Pods/Shared/Hooks/useExplicitMarketingConsent'
import useTracker from 'Pods/Shared/Hooks/useTracker'
import I18n from 'Pods/Shared/I18n'
import ReactPasswordStrengthNew from 'Pods/Shared/ReactPasswordStrengthNew'
import { useRegister } from 'Pods/Shared/User/Hooks/useRegister'

import MTCaptcha from 'Utils/MTCaptcha'
import { NOOP } from 'Utils/stableDefaults'

const CAPTCHA_ID = 'subscription-create-account-captcha'
const SOURCE = 'subscription-guest-checkout'

function CreateAccountStep({
  email,
  planName = '',
  locale = 'en',
  returnPath = '/profile/settings#tab-settings-subscription',
  onCompleted = NOOP,
}) {
  const t = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )

  const { trackEvent } = useTracker()

  useEffect(() => {
    trackEvent(SubscriptionEvents.GUEST_CREATE_ACCOUNT_STEP_VIEWED)
  }, [])

  const handleSuccess = useCallback((response) => {
    trackEvent(SubscriptionEvents.GUEST_CREATE_ACCOUNT_COMPLETED)
    onCompleted(response?.data?.redirectTo || returnPath)
  }, [onCompleted, returnPath, trackEvent])

  const {
    control,
    gdpr,
    registrationCaptchaEnabled,
    passwordError,
    handleSubmit,
    onFormSubmit,
    onPasswordChange,
    onGdprToggle,
  } = useRegister({
    captchaId: CAPTCHA_ID,
    source: SOURCE,
    fromCheckout: false,
    locale,
    onSuccess: handleSuccess,
    defaultEmail: email,
  })

  const explicitConsentRequired = useExplicitMarketingConsent()

  const noticeBody = planName
    ? t('subscription_checkout.create_account.notice_body', { plan: planName })
    : t('subscription_checkout.create_account.notice_body_no_plan')

  return (
    <div className="create-account-step">
      <div className="create-account-step__banner" role="status">
        <Icon svgComponent={SuccessOutlined} customClass="create-account-step__banner-icon" />
        <div className="create-account-step__banner-content">
          <p className="create-account-step__banner-title cgt-body-md-semibold">
            {t('subscription_checkout.create_account.notice_title')}
          </p>
          <p className="create-account-step__banner-body cgt-body-md">
            {noticeBody}{' '}
            <span className="cgt-body-md-semibold">{email}</span>.
          </p>
        </div>
      </div>

      <h1 className="create-account-step__title cgt-heading-300">
        {t('subscription_checkout.create_account.title')}
      </h1>
      <p className="create-account-step__subtitle cgt-body-base">
        {t('subscription_checkout.create_account.subtitle')}
      </p>

      <form className="create-account-step__form" onSubmit={handleSubmit(onFormSubmit)}>
        <Controller
          name="email"
          control={control}
          defaultValue={email}
          render={({ field }) => (
            <TextInput
              label={t('subscription_checkout.create_account.email_label')}
              id="create-account-email"
              name="email"
              value={field.value || email}
              wrapperClassName="create-account-step__email-field"
              handleChange={field.onChange}
              onBlur={field.onBlur}
              readOnly
              disabled
              isPilot
            />
          )}
        />

        <ReactPasswordStrengthNew
          helpText={passwordError}
          hasError={!!passwordError}
          minLength={6}
          className={classnames(
            'cgt-text-input',
            'cgt-text-input--medium',
            'cgt-text-input--primary',
            'cgt-text-input--icon-trail',
            'cgt-text-input--not-spaced',
            'cgt-text-input--password',
            'has-no-bg',
            'create-account-step__password-field'
          )}
          inputProps={{
            id: 'create-account-password',
            name: 'password',
          }}
          changeCallback={onPasswordChange}
          showToggleVisibility
        />

        {explicitConsentRequired && (
          <Toggle
            id="create-account-gdpr-toggle"
            label={t('register.gdpr_label')}
            isChecked={gdpr}
            onChange={onGdprToggle}
            className="create-account-step__gdpr"
          />
        )}

        {registrationCaptchaEnabled && <MTCaptcha captchaId={CAPTCHA_ID} />}

        <Button
          expand
          size="large"
          actionType="submit"
          customClass="create-account-step__submit"
          disabled={explicitConsentRequired === null}
        >
          {t('subscription_checkout.create_account.submit_button')}
        </Button>

        <p className="create-account-step__legal cgt-caption">
          {t('register.terms_prefix')}{' '}
          <a href="/pages/terms-and-conditions" target="_blank" rel="noreferrer" className="cgt-legacy-link">
            {t('register.terms_link')}
          </a>
          {' '}{t('register.terms_middle')}{' '}
          <a href="/pages/privacy-policy" target="_blank" rel="noreferrer" className="cgt-legacy-link">
            {t('register.privacy_link')}
          </a>
        </p>
      </form>
    </div>
  )
}

CreateAccountStep.propTypes = {
  email: string.isRequired,
  planName: string,
  locale: string,
  returnPath: string,
  onCompleted: func,
}

export default CreateAccountStep
